import { useEffect, useState } from "react";
import { User } from "@supabase/supabase-js";
import { useAuthStore } from "../store/authStore";

interface AdminVerificationState {
	user: User | null;
	verifying: boolean;
	verified: boolean;
	mfaRequired: boolean;
	error: string | null;
}

interface VerificationResult {
	userId: string | null;
	verified: boolean;
	mfaRequired: boolean;
	error: string | null;
}

const UNCHECKED: VerificationResult = {
	userId: undefined as unknown as string | null, // non corrisponde a nessun user.id né a null
	verified: false,
	mfaRequired: false,
	error: null,
};

/**
 * Verifica server-side che l'utente loggato sia admin (tabella admin_users),
 * tramite verifyAdminRole() / edge function verify-admin. Non controlla il
 * livello MFA (aal) — quello è responsabilità di AdminRoute, che usa questo
 * hook come base e ci aggiunge il requisito aal2.
 *
 * `verifying` è derivato confrontando l'utente corrente con l'utente a cui
 * si riferisce l'ultimo risultato disponibile, invece di usare un secondo
 * stato locale impostato solo dentro l'effect: altrimenti, nel render subito
 * dopo che `user` passa da null a valorizzato (o cambia), il componente
 * vedrebbe per un istante il risultato "vecchio" (verified=false) prima che
 * l'effect di verifica riparta, causando un redirect/negazione spuri.
 */
export const useAdminVerification = (): AdminVerificationState => {
	const { user, loading, verifyAdminRole } = useAuthStore();
	const [result, setResult] = useState<VerificationResult>(UNCHECKED);
	const [busy, setBusy] = useState(false);

	useEffect(() => {
		let cancelled = false;

		const checkAdminStatus = async () => {
			if (!user) {
				setResult({ userId: null, verified: false, mfaRequired: false, error: null });
				return;
			}

			setBusy(true);
			try {
				const adminVerification = await verifyAdminRole();
				if (cancelled) return;

				// isAdmin è true solo con aal2 completato; mfaRequired distingue
				// "non admin" da "admin ma deve ancora completare/registrare la 2FA".
				setResult({
					userId: user.id,
					verified: adminVerification.isAdmin || !!adminVerification.mfaRequired,
					mfaRequired: !!adminVerification.mfaRequired,
					error:
						adminVerification.isAdmin || adminVerification.mfaRequired
							? null
							: "User is not an administrator",
				});
			} catch (err) {
				console.error("Admin verification error:", err);
				if (cancelled) return;
				setResult({
					userId: user.id,
					verified: false,
					mfaRequired: false,
					error: "Failed to verify admin status",
				});
			} finally {
				if (!cancelled) setBusy(false);
			}
		};

		checkAdminStatus();

		return () => {
			cancelled = true;
		};
	}, [user, verifyAdminRole]);

	// Finché il risultato disponibile non corrisponde all'utente corrente,
	// la verifica è da considerarsi ancora in corso.
	const staleForCurrentUser = (user?.id ?? null) !== result.userId;
	const verifying = loading || busy || staleForCurrentUser;

	return {
		user,
		verifying,
		verified: verifying ? false : result.verified,
		mfaRequired: verifying ? false : result.mfaRequired,
		error: verifying ? null : result.error,
	};
};
